import React from 'react';
import { m } from 'framer-motion';
import { CheckIcon, BookOpenIcon, DumbbellIcon, ClipboardListIcon } from 'lucide-react';
import { SectionTag } from '../ui/SectionTag';
const tomosPillar = {
  icon: BookOpenIcon,
  tag: 'Incluido 📚',
  title: 'Los 6 Tomos del Sistema "Sesión en Mano"',
  value: '59,90€',
  today: 'Hoy: Incluido',
  lead: '180 sesiones profesionales para que nunca te falten ejercicios.',
  items: ['Fundamentos de Oro (6-9 años): El primer contacto con el baloncesto hecho bien.', 'El Motor Técnico (10-12 años): Bote, pase, tiro y defensa construidos con progresión.', 'La Transición Táctica (13-14 años): Contraataque, espaciado, zonas y sistemas colectivos.', 'Construcción Competitiva (15-18 años): Sistemas avanzados, bloqueos y final de partido.', 'Especialidades: Preparación física, tiro avanzado y scouting del rival.', 'Gestión de Equipos: Vestuario, familias, psicología y cierre de temporada.']
};
const bonosPillar = {
  icon: DumbbellIcon,
  tag: 'Incluido 💪',
  title: 'Los 5 Bonos Estratégicos',
  value: '59,50€',
  today: 'Hoy: Incluido',
  lead: 'Los recursos que hacen que el manual funcione desde el primer día.',
  items: ['Plan de Arranque en 7 Días: Por dónde empezar sin agobiarte.', '25 Calentamientos Inteligentes: Con balón y conectados al objetivo de la sesión.', 'Biblioteca de Juego Reducido: 30 situaciones (1v1, 2v2, 3v3) para enseñar a decidir.', 'Ruedas de Finalización Pro: 24 ejercicios de tiro por tipo de jugador y nivel.', 'Sistema Express: 20 sesiones para días de media pista, pocos jugadores o poco tiempo.']
};
const workbookBreakdown = ['6–9 años: 8 sesiones de 45 minutos.', '10–12 años: 8 sesiones de 60 minutos.', '13–14 años: 8 sesiones de 75 minutos.', '15–18 años: 8 sesiones de 90 minutos.', 'Especialidades y scouting: 6 sesiones de 75 minutos.', 'Gestión del equipo: 8 dinámicas guiadas y 10 plantillas imprimibles.'];
const workbookImages = ['https://cdn.magicpatterns.com/uploads/izkeBDbt7bGMTmViCfHqhY/Tecnica-tiro.webp', 'https://cdn.magicpatterns.com/uploads/sjd2NveVmTp9vFzJjvc6XQ/Sesion-15-18-09.webp', 'https://cdn.magicpatterns.com/uploads/izoiRLkdsihU9pHhf1Nsga/Lamina-defensa_(1).webp', 'https://cdn.magicpatterns.com/uploads/4mczM3EXvVvwVGtxdURSDC/Lamina-ataque.webp'];
function ContentPillar({
  pillar,
  delay
}: {
  pillar: typeof tomosPillar;
  delay: number;
}) {
  return <m.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.45,
    delay
  }} className="flex flex-col rounded-2xl border border-white/10 bg-bg-secondary p-6 sm:p-7" data-id="element-281" data-mp="2l6xflm8o0dft">
      <div className="flex items-start justify-between gap-3" data-id="element-282" data-mp="2q8wikv0ht8c8">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-orange/10" data-id="element-283" data-mp="zvfjzmktc52i">
          <pillar.icon size={22} className="text-orange" data-id="element-284" data-mp="vu3pp3qzuvnz" />
        </div>
        <span className="rounded-full bg-white/5 px-3 py-1 text-xs font-semibold text-muted" data-id="element-285" data-mp="vvwuselyl7tv">
          {pillar.tag}
        </span>
      </div>
      <h3 className="mt-4 font-heading text-lg font-semibold leading-snug text-white sm:text-xl" data-id="element-286" data-mp="prqn94jqut83">
        {pillar.title}
      </h3>
      <div className="mt-2 flex items-baseline gap-2 text-sm" data-id="element-287" data-mp="bzkhmce7w4mw">
        <span className="text-muted line-through" data-id="element-288" data-mp="gydmxhjcopbz">Valor: {pillar.value}</span>
        <span className="font-semibold text-success" data-id="element-289" data-mp="2263ijfrnrxr">{pillar.today}</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-290" data-mp="2492vu2t6xf5">{pillar.lead}</p>
      <ul className="mt-4 space-y-2.5" data-id="element-291" data-mp="72anwkjsp6t9">
        {pillar.items.map(item => <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-white/90" data-id="element-292" data-mp="3p7xbbn3bq988">
            <CheckIcon size={16} className="mt-0.5 flex-shrink-0 text-success" data-id="element-293" data-mp="3u68nw5l06xu5" />
            <span data-id="element-294" data-mp="1ngaidfzvg494">{item}</span>
          </li>)}
      </ul>
    </m.div>;
}
export function WhatYouGet() {
  return <section className="bg-bg-primary py-10 sm:py-14" data-id="element-295" data-mp="1sdtyymk1kt48">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6" data-id="element-296" data-mp="3oi0a0v28h47l">
        <SectionTag icon="🎁" data-id="element-297" data-mp="3of4bb4ikabi3">Lo que recibes hoy (Acceso Inmediato)</SectionTag>
        <h2 className="mt-5 font-heading text-3xl font-bold uppercase leading-tight text-white sm:text-4xl" data-id="element-298" data-mp="3jj6tetrngx37">
          Todo lo que necesitas, en un solo lugar.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg" data-id="element-299" data-mp="3agxm0crohju1">
          180 sesiones para que preparar el entrenamiento deje de comerse tu tiempo.
        </p>
        <p className="mt-2 text-sm font-medium text-orange" data-id="element-300" data-mp="30re171kenfwj">
          Más de 180 sesiones · 6 Tomos · +10 Meses de entrenamientos · 100% Editable
        </p>
      </div>

      <div className="mx-auto mt-8 flex max-w-3xl flex-col gap-6 px-4 sm:px-6" data-id="element-301" data-mp="2vqgjsr77ypw3">
        <ContentPillar pillar={tomosPillar} delay={0} data-id="element-302" data-mp="2qvjbxnloemks" />

        <m.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        delay: 0.08
      }} className="flex flex-col rounded-2xl border border-success/30 bg-bg-secondary p-6 sm:p-7" data-id="element-303" data-mp="2mseew0kd7d3j">
          <div className="flex items-start justify-between gap-3" data-id="element-304" data-mp="12916e4h73so1">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-success/10" data-id="element-305" data-mp="16bxgt7wl0igw">
              <ClipboardListIcon size={22} className="text-success" data-id="element-306" data-mp="1bbadekd993wo" />
            </div>
            <span className="rounded-full bg-success/10 px-3 py-1 text-xs font-semibold text-success" data-id="element-307" data-mp="ekrnpbfa3l14">
              Nuevo: Incluido
            </span>
          </div>
          <h3 className="mt-4 font-heading text-lg font-semibold leading-snug text-white sm:text-xl" data-id="element-308" data-mp="ik0hggabjiwo">
            6 Cuadernos Prácticos Visuales
          </h3>
          <p className="mt-1 text-sm font-medium text-success" data-id="element-309" data-mp="scy48ymcfu13">Del manual a la pista, con entrenamientos preparados.</p>

          <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-310" data-mp="3r1de8lqb35fi">
            Además de los tomos, recibes seis cuadernos complementarios con 38 sesiones adicionales
            repartidas por edades y objetivos, más 8 dinámicas de comunicación y organización del equipo.
          </p>

          <ul className="mt-4 space-y-2" data-id="element-311" data-mp="y6myvw8usldn">
            {workbookBreakdown.map(item => <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-white/90" data-id="element-312" data-mp="1bxrsohpjijg2">
                <CheckIcon size={16} className="mt-0.5 flex-shrink-0 text-success" data-id="element-313" data-mp="17yizbdyewnl1" />
                <span data-id="element-314" data-mp="k6vhdlj4omuw">{item}</span>
              </li>)}
          </ul>

          <p className="mt-4 text-sm leading-relaxed text-muted" data-id="element-315" data-mp="f93nwn2u4sol">
            Los cuadernos de entrenamiento incluyen tiempos por bloque, organización de jugadores,
            ejercicios con diagramas, rotaciones y ajustes de dificultad.
          </p>
          <p className="mt-2 text-sm font-medium text-white" data-id="element-316" data-mp="o7xh03gyj0xe">
            240 páginas adicionales para consultar, imprimir y utilizar al preparar tus entrenamientos.
          </p>

          <p className="mx-auto mt-5 w-fit rounded-full bg-success/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-success" data-id="element-317" data-mp="1znm3ktqatxxo">
            Incluidos con tu compra
          </p>

          <div className="mt-4 scrollbar-hide flex gap-3 overflow-x-auto pb-1" data-id="element-318" data-mp="2c7stkv462im4">
            {workbookImages.map((src, i) => <img key={src} src={src} alt={`Página real de uno de los 6 cuadernos prácticos (ejemplo ${i + 1})`} loading="lazy" decoding="async" className="aspect-[3/4] w-[180px] flex-shrink-0 rounded-lg border border-white/10 object-cover sm:w-[210px]" data-id="element-319" data-mp="1y9co17i1iy5k" data-mpi={i} />)}
          </div>
          <p className="mt-2 text-center text-xs text-muted" data-id="element-320" data-mp="2h5udtjdbwnz6">Páginas reales de los cuadernos incluidos.</p>
        </m.div>

        <ContentPillar pillar={bonosPillar} delay={0.16} data-id="element-321" data-mp="39uruji1w6cpt" />
      </div>
    </section>;
}